import React, { lazy, ReactNode, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';

import SigningOutSpinnerOverlay from '#/components/auth/SigningOutSpinnerOverlay';
import DepositBackgroundWorker from '#/components/balances/deposit/DepositBackgroundWorker';
import DepositMobilePage from '#/components/balances/deposit/DepositMobilePage';
import WithdrawalBackgroundWorker from '#/components/balances/withdraw/WithdrawalBackgroundWorker';
import WithdrawMobilePage from '#/components/balances/withdraw/WithdrawMobilePage';
import DashboardIndexRedirect from '#/components/layout/dashboard/DashboardIndexRedirect';
import DashboardPage from '#/components/layout/dashboard/DashboardPage';
import ErrorBoundary from '#/components/layout/ErrorBoundary';
import { BaseLayout } from '#/components/layout/Layout';
import MainLayout from '#/components/layout/main/MainLayout';
import MainLayoutMobile from '#/components/layout/main/MainLayoutMobile';
import { getChainThemeSelector } from '#/components/layout/main/theme';
import MaintenancePage from '#/components/layout/MaintenancePage';
import NotFoundPage from '#/components/layout/NotFoundPage';
import ErrorOverlayModal from '#/components/layout/notice/ErrorOverlayModal';
import NotificationsContainer from '#/components/layout/notifications/NotificationsContainer';
import ScreenSizeDetector from '#/components/layout/ScreenSizeDetector';
import MarketDetailsPage from '#/components/markets/detail/MarketDetailsPage';
import MarketsPage from '#/components/markets/marketsScreen/MarketsPage';
import ReferralAccessRedirectPage from '#/components/rewards/referral/ReferralAccessRedirectPage';
import RewardsMenuPageMobile from '#/components/rewards/RewardsMenuPageMobile';
import MorePageMobile from '#/components/settings/MorePageMobile';
import DelegatedKeysBackgroundWorker from '#/components/wallet/authorizations/DelegatedKeysBackgroundWorker';
import WalletDashboardPage from '#/components/wallet/dashboard/WalletDashboardPage';
import WalletModalAddressIncorrect from '#/components/wallet/modal/WalletModalAddressIncorrect';
import UnlockModal from '#/components/wallet/unlock/UnlockModal';
import UnlockPage from '#/components/wallet/unlock/UnlockPage';
import WalletNetworkChainWatcher from '#/components/wallet/WalletNetworkChainWatcher';
import WalletSignPromptOverlayModal from '#/components/wallet/WalletSignPromptOverlayModal';
import { SHOW_DEBUG_PANELS } from '#/config';
import {
  CODE_REQUIRED_OVERLAY,
  MAINTENANCE_MODE,
  VAULTS_ENABLED,
} from '#/config/constants';
import WalletConnectQrModal from '#/lib/ethereum/wallets/WalletConnectQrModal';
import { useSelector } from '#/reduxState/store';

import { useSelectorActiveChain } from './hooks';
import BankrollAccessRedirectPage from './rewards/bankroll/BankrollAccessRedirectPage';
import BankrollWorker from './rewards/bankroll/BankrollWorker';
import SettingsModal from './settings/SettingsModal';
import { AutomaticKatanaConnectPageWithRedirect } from './wallet/mobileSync/automaticQr';
import QrSyncDesktop from './wallet/mobileSync/desktop/QrSyncDesktop';
import WebsocketSubscriptionManager from './WebsocketSubscriptionManager';

const DevBar = lazy(() => import('#/components/dev/devbar/DevBar'));
const AppRoutesDev = lazy(() => import('./AppRoutesDev'));
const AppRoutesSupport = lazy(() => import('./AppRoutesSupport'));
const LegalPage = lazy(() => import('#/components/auth/Legal/LegalPage'));
const VaultsPage = lazy(() => import('#/components/vaults/VaultsPage'));
const RewardsPage = lazy(() => import('#/components/rewards/RewardsPage'));
const XCallbackPage = lazy(
  () => import('#/components/vaults/social/XCallbackPage')
);

const RedirectWithParamsToWalletPrefix = () => {
  const { hash, pathname, search } = useLocation();
  const path = pathname.startsWith('/user/')
    ? pathname.replace('/user', '')
    : pathname;
  return <Navigate to={`/wallet/api-keys${path}${search}${hash}`} replace />;
};

/** Add required providers and theme classes */
const RoutesWrap = ({ children }: { children: ReactNode }) => {
  const { activeChain } = useSelectorActiveChain();
  const isMobile = useSelector(state => state.screen.isMobile);

  const classesForRootAndModals = `${getChainThemeSelector(activeChain)} ${
    isMobile ? 'isMobileStyle' : 'isDesktopStyle'
  }`;

  useEffect(() => {
    const portalRoot = document.getElementById('rootModals');
    if (portalRoot) {
      portalRoot.className = classesForRootAndModals;
    }
  }, [classesForRootAndModals]);

  return (
    <div className={`bodyWrap ${classesForRootAndModals}`}>{children}</div>
  );
};

const UnmatchedRoute = () => {
  const location = useLocation();
  const isMobilePage = location.pathname.startsWith('/mobile-');
  const isMobileState = useSelector(state => state.screen.isMobile);

  if (isMobilePage && !isMobileState) {
    // When user viewport changed from mobile to desktop
    // we should not longer dispay such page
    return <Navigate replace to="/" />;
  }

  return isMobilePage ? (
    // Special mobile pages starting with "/mobile-"
    <MobileRoutes />
  ) : (
    <BaseLayout>
      <NotFoundPage />
    </BaseLayout>
  );
};

const MobileRoutes = () => (
  <Routes>
    <Route
      path="/mobile-rewards-menu"
      element={
        <BaseLayout>
          <RewardsMenuPageMobile />
        </BaseLayout>
      }
    />
    <Route
      path="/mobile-more"
      element={
        <BaseLayout>
          <MorePageMobile />
        </BaseLayout>
      }
    />
    <Route
      path="/mobile-more/settings"
      element={
        <BaseLayout>
          <SettingsModal />
        </BaseLayout>
      }
    />
    {/* <Route
      path="/mobile-buy-crypto"
      element={
        <BaseLayout>
          <BuyCryptoPageMobile />
        </BaseLayout>
      }
    /> */}

    <Route
      element={
        <BaseLayout>
          <NotFoundPage />
        </BaseLayout>
      }
    />
  </Routes>
);

const MainLayoutRoutes = () => (
  <Routes>
    <Route path="/wallet">
      <Route
        index
        path="*"
        element={
          <BaseLayout>
            <WalletDashboardPage />
          </BaseLayout>
        }
      />
    </Route>
    <Route
      path="/katana-connect"
      element={<AutomaticKatanaConnectPageWithRedirect />}
    />
    {/* Mobile page used mostly on mobile. Prefer usage of Modal instead (showWalletModal) */}
    <Route
      path="/unlock"
      element={
        <BaseLayout>
          <UnlockPage />
        </BaseLayout>
      }
    />
    <Route
      path="/"
      element={
        <BaseLayout>
          <DashboardIndexRedirect />
        </BaseLayout>
      }
    />
    <Route
      path="/trading"
      element={
        <BaseLayout>
          <DashboardIndexRedirect />
        </BaseLayout>
      }
    />
    {/* Old trading paths redirects */}
    <Route
      path="/trading/:baseAndQuote"
      element={<Navigate replace to="/" />}
    />
    <Route
      path="/polygon/trading"
      element={
        <BaseLayout>
          <DashboardIndexRedirect />
        </BaseLayout>
      }
    />
    <Route
      path="/eth/trading/:baseAndQuote?"
      element={<Navigate replace to="/polygon/trading" />}
    />
    <Route
      path="/bsc/trading/:baseAndQuote?"
      element={<Navigate replace to="/polygon/trading" />}
    />
    <Route
      path="/polygon/trading/:baseAndQuote?"
      element={<Navigate replace to="/trade" />}
    />
    {/* <Route path="/trade" element={<Navigate replace to="/wallet" />} />
    <Route path="/trade/*" element={<Navigate replace to="/wallet" />} /> */}
    <Route
      path="/trade/:baseAndQuote"
      // Note that this specific component will not have scrolling enabled - see MainLayout
      element={
        <BaseLayout>
          <DashboardPage />
        </BaseLayout>
      }
    />
    <Route
      path="/trade/:baseAndQuote/mobile-trade"
      element={
        <BaseLayout>
          <DashboardPage />
        </BaseLayout>
      }
    />
    <Route
      path="/markets"
      element={
        <BaseLayout>
          <MarketsPage />
        </BaseLayout>
      }
    />
    <Route
      path="/markets/:marketPairRaw"
      element={
        <BaseLayout>
          <MarketDetailsPage />
        </BaseLayout>
      }
    />
    <Route
      path="/vaults/x-callback"
      element={
        <BaseLayout>
          <XCallbackPage />
        </BaseLayout>
      }
    />
    <Route path="/vaults/:vaultManagerWallet?">
      <Route
        index
        path="*"
        element={
          VAULTS_ENABLED ? (
            <BaseLayout>
              <VaultsPage />
            </BaseLayout>
          ) : (
            'This feature is disabled'
          )
        }
      />
    </Route>
    <Route path="/rewards">
      <Route
        index
        path="*"
        element={
          <BaseLayout>
            <RewardsPage />
          </BaseLayout>
        }
      />
    </Route>
    <Route
      path="/bankroll/:code"
      element={
        <BaseLayout>
          <BankrollAccessRedirectPage type="bankroll" />
        </BaseLayout>
      }
    />
    <Route
      path="/welcome/:code"
      element={
        <BaseLayout>
          <BankrollAccessRedirectPage type="welcomePoints" />
        </BaseLayout>
      }
    />
    {/* Redirect old routes to new path (Confirm emails might be using those) */}
    <Route path="/user">
      <Route index path="*" element={<RedirectWithParamsToWalletPrefix />} />
    </Route>
    <Route path="/login" element={<RedirectWithParamsToWalletPrefix />} />
    <Route path="/signup" element={<RedirectWithParamsToWalletPrefix />} />
    {/* V3 routes redirects */}
    <Route path="/trades" element={<Navigate replace to="/wallet/history" />} />
    <Route
      path="/activity"
      element={<Navigate replace to="/wallet/history" />}
    />
    <Route
      path="/balances"
      element={<Navigate replace to="/wallet/positions" />}
    />
    <Route path="/liquidity" element={<Navigate replace to="/" />} />
    <Route path="/farms" element={<Navigate replace to="/" />} />
    <Route
      path="/r/:code"
      element={
        <BaseLayout>
          <ReferralAccessRedirectPage />
        </BaseLayout>
      }
    />
    <Route
      path="/legal"
      element={
        <BaseLayout>
          <LegalPage />
        </BaseLayout>
      }
    />
    <Route path="*" element={<UnmatchedRoute />} />
  </Routes>
);

export default function App() {
  const { chainIsListed } = useSelectorActiveChain();
  const signingOut = useSelector(
    state => state.currentUserData.userProcesses.signingOut
  );
  const settingsOpen = useSelector(state => state.userSettings.settingsOpen);

  if (MAINTENANCE_MODE) {
    return (
      <RoutesWrap>
        <MaintenancePage />
      </RoutesWrap>
    );
  }
  // console.log('NODE_ENV', process.env.NODE_ENV);
  return (
    <RoutesWrap>
      {SHOW_DEBUG_PANELS && <DevBar />}

      {signingOut && <SigningOutSpinnerOverlay />}

      <NotificationsContainer />

      {/* Background workers which not render anything visible */}
      <ScreenSizeDetector />
      {chainIsListed && (
        <>
          <DepositBackgroundWorker />
          <WithdrawalBackgroundWorker />
          <WebsocketSubscriptionManager />
        </>
      )}
      {settingsOpen && <SettingsModal />}
      <WalletSignPromptOverlayModal />
      <DelegatedKeysBackgroundWorker />
      <WalletNetworkChainWatcher />
      <BankrollWorker />
      {/* <WalletModalChainIdIncorrect /> */}
      <WalletModalAddressIncorrect />
      <UnlockModal />
      <QrSyncDesktop />
      <ErrorOverlayModal />
      <WalletConnectQrModal />
      <Routes>
        {/* Routes with no predefined layout */}

        {/* <Route path="/leaderboard" element={<Leaderboard />} /> */}

        <Route
          path="/check"
          element={<Navigate replace to="/support/check" />}
        />

        <Route path="/dev">
          <Route path="*" element={<AppRoutesDev />} />
        </Route>
        <Route path="/support">
          <Route path="*" element={<AppRoutesSupport />} />
        </Route>

        <Route
          path="/mobile-deposit"
          element={
            <MainLayoutMobile>
              <ErrorBoundary displayErrorMessage>
                <DepositMobilePage />
              </ErrorBoundary>
            </MainLayoutMobile>
          }
        />

        <Route
          path="/mobile-withdraw"
          element={
            <MainLayoutMobile footer={false}>
              <ErrorBoundary displayErrorMessage>
                <WithdrawMobilePage />
              </ErrorBoundary>
            </MainLayoutMobile>
          }
        />

        <Route
          path="/terms"
          element={
            CODE_REQUIRED_OVERLAY ? <LegalPage /> : <Navigate to="/legal" />
          }
        />

        {/* Routes with standard layout (Header & Footer) */}
        <Route
          path="*"
          element={
            <MainLayout>
              <ErrorBoundary displayErrorMessage>
                <MainLayoutRoutes />
              </ErrorBoundary>
            </MainLayout>
          }
        />
      </Routes>
    </RoutesWrap>
  );
}
